import { authErrorString } from '../AuthErrors';
import { Logger } from '../Logger';
import { logConstants } from '../../utils/Constants';

/**
 * Function that translates URLs originating from external network to access storefront
 * @param {gatewayUrl} URL - URL of the gateway
 * @param {directUrl} URL - URL of the storefront resource that is to be accessed
 * @param {rewriteMode} string - rewriteMode decides whether the translation should be done in SG mode or entcvpn mode
 * @returns {URL} - translated URL
 */
export function translateUrlSFToGateway(
	gatewayUrl: URL,
	directUrl: URL,
	rewriteMode: string
): URL {
	try {
		switch (rewriteMode) {
			case 'sg':
				return translateUrlSGMode(gatewayUrl, directUrl);
			case 'entcvpn': {
				const prefix = 'cvpn'; // ! TODO - set prefix to cvpn or securebrowse based on the flags
				return translateUrlEntcvpnMode(gatewayUrl, directUrl, prefix);
			}
			default:
				throw new Error(authErrorString.UNEXPECTED_PROXY_TRANSLATE_URL);
		}
	} catch (e) {
		Logger.getInstance().write(
			logConstants.ERROR,
			'Error while translating the URL ' + e.message
		);
	}
}

/**
 * Function that translates external URLs in SG mode
 * @param {gatewayUrl} URL - URL of the gateway
 * @param {directUrl} URL - URL of the storefront resource that is to be accessed
 * @returns {URL} - translated URL
 */
function translateUrlSGMode(gatewayUrl: URL, directUrl: URL): URL {
	try {
		const gatewayPort = gatewayUrl.port ? `:${gatewayUrl.port}` : '';
		const translatedUrl =
			gatewayUrl.protocol +
			'//' +
			gatewayUrl.hostname +
			gatewayPort +
			directUrl.pathname +
			directUrl.search;
		return new URL(translatedUrl);
	} catch (e) {
		Logger.getInstance().write(
			logConstants.ERROR,
			'Error while translating the URL SG Mode' + e.message
		);
		throw e;
	}
}

/**
 * Function that translates external URLs in cvpn mode given the prefix
 * @param {gatewayUrl} URL - URL of the gateway
 * @param {directUrl} URL - URL of the storefront resource that is to be accessed
 * @param {prefix} string - prefix based on the rewrite mode
 * @returns {URL} - translated URL
 */
function translateUrlEntcvpnMode(gatewayUrl: URL, directUrl: URL, prefix: string): URL {
	try {
		const gatewayPort = gatewayUrl.port ? `:${gatewayUrl.port}` : '';
		const directPort = directUrl.port ? `:${gatewayUrl.port}` : '';
		const translatedUrl =
			gatewayUrl.protocol +
			'//' +
			gatewayUrl.hostname +
			gatewayPort +
			'/' +
			prefix +
			'/' +
			directUrl.protocol.slice(0, directUrl.protocol.length - 1) +
			'/' +
			directUrl.hostname +
			directPort +
			directUrl.pathname +
			directUrl.search;
		return new URL(translatedUrl);
	} catch (e) {
		Logger.getInstance().write(
			logConstants.ERROR,
			'Error while translating the URL Ent CVPN Mode' + e.message
		);
		throw e;
	}
}

export function translateUrlGatewayToSF(
	gatewayUrl: URL,
	directUrl: URL,
	rewriteMode: string
): URL {
	try {
		switch (rewriteMode) {
			case 'sg':
				return translateUrlGatewaytoSF_SGMode(gatewayUrl, directUrl);
			case 'entcvpn': {
				const prefix = 'cvpn'; // ! TODO - set prefix to cvpn or securebrowse based on the flags
				return translateUrlGatewayToSF_EntcvpnMode(gatewayUrl, prefix);
			}
			default:
				throw new Error(authErrorString.UNEXPECTED_PROXY_TRANSLATE_URL);
		}
	} catch (e) {
		Logger.getInstance().write(
			logConstants.ERROR,
			'Error while translating the URL ' + e.message
		);
	}
}

function translateUrlGatewaytoSF_SGMode(gatewayUrl: URL, directUrl: URL): URL {
	try {
		const directPort = directUrl.port ? `:${directUrl.port}` : '';
		const translatedUrl =
			directUrl.protocol +
			'//' +
			directUrl.hostname +
			directPort +
			gatewayUrl.pathname +
			gatewayUrl.search;

		return new URL(translatedUrl);
	} catch (e) {
		Logger.getInstance().write(
			logConstants.ERROR,
			'Error while translating the URL in SG Mode from SF to gateway' + e.message
		);
		throw e;
	}
}

function translateUrlGatewayToSF_EntcvpnMode(gatewayUrl: URL, prefix: string): URL {
	try {
		const regExp = new RegExp(`\/${prefix}\/(.*?)\/(.*)`);
		const match = gatewayUrl.pathname.match(regExp);

		if (match) {
			const protocol = match[1];
			const url = match[2];

			const translatedUrl = protocol + '://' + url;
			return new URL(translatedUrl);
		} else {
			throw new Error('Invalid gateway URL format');
		}
	} catch (e) {
		Logger.getInstance().write(
			logConstants.ERROR,
			'Error while translating the URL in Ent CVPN Mode from SF to gateway' + e.message
		);
		throw e;
	}
}
